import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Flex,
  IconButton,
  NoResults,
  SkeletonLoader,
} from "@bdo/cornerstone-ui";
import {
  RiErrorWarningLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "@remixicon/react";
import { useGetWorkflowsQuery } from "@/app/workflows/workflowsListApi";
import { selectWorkflowSearchFilter, WorkflowCard } from "@/features";
import {
  useDebounceValue,
  useGridColumns,
  useWorkflowSearchParams,
} from "@/hooks";
import type { WorkflowInstance } from "@/types";

function getLatestStep(workflow: WorkflowInstance) {
  const lastModified = workflow.workflowStepInstances.reduce(
    (prev, current) => {
      if (!prev.modifiedOn) return current;
      if (!current.modifiedOn) return prev;

      return new Date(prev.modifiedOn) > new Date(current.modifiedOn)
        ? prev
        : current;
    },
    workflow.workflowStepInstances[0],
  );

  return lastModified;
}

export function WorkflowCards() {
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.workflows.workflowCards",
  });

  const { ref: gridRef, top: TOP } = useGridColumns();

  const searchFilter = useSelector(selectWorkflowSearchFilter);
  const {
    orderBy,
    pagination,
    statusFilters,
    workflowDefinitionFilters,
    handlePrevious,
    handleNext,
  } = useWorkflowSearchParams({
    top: TOP,
  });

  const navigate = useNavigate();

  function handleNewWorkflow() {
    navigate("/workflow/create");
  }

  const debouncedSearchInput = useDebounceValue(searchFilter, 500);

  const odataSearchFilters = [
    `contains(tolower(workflowInstanceName),'${debouncedSearchInput.toLowerCase()}')`,
    `contains(tolower(workflowDefinition/workflowDefinitionName),'${debouncedSearchInput.toLowerCase()}')`,
  ];

  const filterParts: string[] = [];

  if (odataSearchFilters.length) {
    filterParts.push(`(${odataSearchFilters.join(" or ")})`);
  }
  if (statusFilters.length) {
    filterParts.push(`(${statusFilters.join(" or ")})`);
  }
  if (workflowDefinitionFilters.length) {
    filterParts.push(`(${workflowDefinitionFilters.join(" or ")})`);
  }

  const filter = filterParts.join(" and ");

  const {
    data: workflows,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetWorkflowsQuery({
    filter: filter,
    expand: "WorkflowDefinition,WorkflowStepInstances",
    orderBy: orderBy,
    top: pagination.top + 1, // Fetch one extra item to determine if there's a next page
    skip: pagination.skip,
  });

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchInput]);

  if (error && !isLoading) {
    return (
      <Flex direction="column" justify="center" align="center" gap="xs" grow>
        <RiErrorWarningLine className="text-[64px] text-primary-darker" />
        <h2 className="text-lg text-mono-dark font-bold">
          {t("error.heading")}
        </h2>
        <p className="text-mono-dark">{t("error.description")}</p>
      </Flex>
    );
  }

  if (workflows?.length === 0 && !isLoading && !isFetching) {
    return (
      <Flex align="center" gap="xs" grow>
        <Flex direction="column" align="center">
          <NoResults />
          <h2 className="text-lg text-mono font-bold">
            {debouncedSearchInput ||
            statusFilters.length ||
            workflowDefinitionFilters.length
              ? t("noResults.heading")
              : t("noWorkflows.heading")}
          </h2>
          <p className="text-mono-dark">
            {debouncedSearchInput ||
            statusFilters.length ||
            workflowDefinitionFilters.length
              ? t("noResults.description")
              : t("noWorkflows.description")}
          </p>
          <span>
            {t("noWorkflows.newWorkflowSection.preButtonText")}
            <button
              className="text-link hover:underline"
              onClick={handleNewWorkflow}
              data-testid="no-results-new-workflow-button"
            >
              {t("noWorkflows.newWorkflowSection.buttonText")}
            </button>
          </span>
        </Flex>
      </Flex>
    );
  }

  return (
    <div className="w-full flex justify-center items-center flex-col gap-sm mb-md">
      <div
        className="px-lg py-sm grid gap-sm w-full"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}
        ref={gridRef}
        data-testid="workflow-list"
      >
        {isLoading || isFetching
          ? Array.from({ length: 12 }, (_, index) => (
              <SkeletonLoader
                key={index}
                height="158px"
                width="100%"
                className="p-lg min-[1180px]:max-w-[400px]"
              />
            ))
          : // We need to trim off the extra item used to check for next page
            [...(workflows ?? [])]
              .slice(0, pagination.top)
              .map((workflow) => (
                <WorkflowCard
                  key={workflow.id}
                  id={workflow.id}
                  title={workflow.workflowInstanceName}
                  workflowDefinitionName={
                    workflow.workflowDefinition.workflowDefinitionName
                  }
                  stepInstance={getLatestStep(workflow)}
                  isAwaitingAttention={workflow.status === "RequiresAttention"}
                  modifiedOn={workflow.modifiedOn}
                  version={workflow.workflowDefinition.version}
                />
              ))}
      </div>
      {workflows &&
        (workflows.length > pagination.top || pagination.skip > 0) && (
          <div
            className="w-full flex flex-row flex-nowrap justify-end px-lg gap-md"
            data-testid="pagination-container"
          >
            <IconButton
              size="sm"
              icon={<RiArrowLeftSLine />}
              onClick={handlePrevious}
              disabled={pagination.skip === 0}
              aria-label="Previous page"
            />
            <IconButton
              size="sm"
              icon={<RiArrowRightSLine />}
              onClick={handleNext}
              disabled={!workflows || workflows.length <= pagination.top}
              aria-label="Next page"
            />
          </div>
        )}
    </div>
  );
}
